import React, { useCallback, useEffect, useRef, useState } from "react";
import { Bot, Clock, MessageSquare, Hash, Mic, Type } from "lucide-react";
import { useLocation } from "react-router-dom";
import "../../assets/css/KnowledgeBase.css";
import { getProjectPid } from "../../utils/sessionProject";

const BASE_URL = process.env.REACT_APP_BASE_URL;
const LIMIT = 20;

const formatTime = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true });
};

const formatDate = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const PromptHistoryDetails = () => {
  const location = useLocation();
  const user = location.state?.user;

  const token = localStorage.getItem("token");
  const projectPid = getProjectPid();

  // Sessions dropdown
  const [sessions, setSessions] = useState([]);
  const [pagination, setPagination] = useState({ hasNextPage: false });
  const [sessionsPage, setSessionsPage] = useState(1);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [activeSession, setActiveSession] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Messages
  const [messages, setMessages] = useState([]);
  const [sessionInfo, setSessionInfo] = useState(null);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const messagesEndRef = useRef(null);

  // Fetch sessions list
  const fetchSessions = useCallback(async (pg) => {
    if (!user?.user_pid || !projectPid) { setLoadingSessions(false); return; }
    setLoadingSessions(true);
    try {
      const res = await fetch(
        `${BASE_URL}/api/v1/chat/projects/${projectPid}/users/${user.user_pid}/sessions?page=${pg}&limit=${LIMIT}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (data.status) {
        const items = data.response?.items || [];
        setSessions(prev => pg === 1 ? items : [...prev, ...items]);
        setPagination(data.response?.pagination || {});
        if (pg === 1 && items.length > 0) setActiveSession(items[0]);
      }
    } catch {}
    setLoadingSessions(false);
  }, [user?.user_pid, projectPid, token]);

  useEffect(() => { fetchSessions(1); }, [fetchSessions]);

  // Fetch messages when active session changes
  useEffect(() => {
    if (!activeSession) return;
    const sessionId = activeSession.session_pid || activeSession.id;
    setLoadingMessages(true);
    setMessages([]);
    setSessionInfo(null);
    fetch(`${BASE_URL}/api/v1/chat/sessions/${sessionId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(data => {
        if (data.status) {
          setMessages(data.response?.messages || []);
          setSessionInfo(data.response?.session || null);
        }
      })
      .catch(() => {})
      .finally(() => setLoadingMessages(false));
  }, [activeSession, token]);

  // Scroll to bottom when messages load
  useEffect(() => {
    if (messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const loadMore = () => {
    if (pagination.hasNextPage) {
      const next = sessionsPage + 1;
      setSessionsPage(next);
      fetchSessions(next);
    }
  };

  return (
    <main className="app-wrapper phd3-page">
      <div className="phd3-layout">

        {/* Session Dropdown */}
        <div className="phd3-dropdown-row" ref={dropdownRef}>
          <button
            className="phd3-session-dropdown-btn"
            onClick={() => setDropdownOpen(o => !o)}
          >
            <div className="phd3-dropdown-selected">
              {activeSession ? (
                <>
                  <span className={`phd3-type-badge phd3-type-${activeSession.type}`}>
                    {activeSession.type === "voice" ? <Mic size={11} /> : <Type size={11} />}
                    {activeSession.type}
                  </span>
                  <span className="phd3-dropdown-title">{activeSession.title}</span>
                  <span className="phd3-dropdown-time">
                    <Clock size={11} /> {formatDate(activeSession.started_at)}, {formatTime(activeSession.started_at)}
                  </span>
                </>
              ) : (
                <span className="text-muted" style={{ fontSize: 13 }}>
                  {loadingSessions ? "Loading sessions..." : "Select a session"}
                </span>
              )}
            </div>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, transition: "transform 0.15s", transform: dropdownOpen ? "rotate(180deg)" : "rotate(0deg)" }}>
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>

          {dropdownOpen && (
            <ul className="phd3-session-menu">
              {sessions.map(s => (
                <li
                  key={s.id}
                  className={`phd3-session-option${s.id === activeSession?.id ? " active" : ""}`}
                  onClick={() => { setActiveSession(s); setDropdownOpen(false); }}
                >
                  <span className={`phd3-type-badge phd3-type-${s.type}`}>
                    {s.type === "voice" ? <Mic size={11} /> : <Type size={11} />}
                    {s.type}
                  </span>
                  <div className="phd3-option-info">
                    <span className="phd3-option-title">{s.title}</span>
                    <span className="phd3-option-meta">
                      <Clock size={10} /> {formatDate(s.started_at)}, {formatTime(s.started_at)}
                      &nbsp;·&nbsp;
                      <Hash size={10} /> {s.message_count} msgs
                    </span>
                  </div>
                </li>
              ))}
              {pagination.hasNextPage && (
                <li className="phd3-load-more" onClick={loadMore}>Load more sessions…</li>
              )}
              {sessions.length === 0 && !loadingSessions && (
                <li className="phd3-option-empty">No sessions found.</li>
              )}
            </ul>
          )}
        </div>

        {/* Chat Area */}
        <div className="phd3-chat-area">
          {!activeSession ? (
            <div className="phd3-chat-empty">
              <div className="phd3-chat-empty-ico"><MessageSquare size={28} /></div>
              <p className="phd3-chat-empty-title">No session selected</p>
              <p className="phd3-chat-empty-sub">Pick a session from the dropdown above</p>
            </div>
          ) : (
            <>
              {/* Chat header */}
              <div className="phd3-chat-head">
                <div className="phd3-chat-head-left">
                  <div className="phd3-chat-head-ico"><Bot size={17} /></div>
                  <div>
                    <h2 className="phd3-chat-title">{activeSession.title}</h2>
                    <p className="phd3-chat-sub">
                      <Clock size={11} /> {formatDate(activeSession.started_at)}, {formatTime(activeSession.started_at)}
                    </p>
                  </div>
                </div>
                <div className="phd3-chat-head-right">
                  {sessionInfo && (
                    <span className="phd3-msgs-pill">
                      <Hash size={11} /> {sessionInfo.total_messages} messages
                    </span>
                  )}
                  <span className={`phd3-type-badge phd3-type-${activeSession.type}`}>
                    {activeSession.type === "voice" ? <Mic size={11} /> : <Type size={11} />}
                    {activeSession.type}
                  </span>
                </div>
              </div>

              {/* Messages */}
              <div className="phd3-messages">
                {loadingMessages ? (
                  <div className="phd3-chat-empty">
                    <p className="phd3-chat-empty-sub">Loading messages...</p>
                  </div>
                ) : messages.length === 0 ? (
                  <div className="phd3-chat-empty">
                    <div className="phd3-chat-empty-ico"><MessageSquare size={28} /></div>
                    <p className="phd3-chat-empty-title">No messages</p>
                    <p className="phd3-chat-empty-sub">This session has no messages.</p>
                  </div>
                ) : (
                  messages.map((msg) => (
                    <div key={msg.id} className={`phd3-msg-row phd3-msg-${msg.role}`}>
                      <div className="phd3-msg-wrap">
                        <span className="phd3-role-label">
                          {msg.role === "assistant" ? "AI" : msg.role.charAt(0).toUpperCase() + msg.role.slice(1)}
                        </span>
                        <div className={`phd3-bubble phd3-bubble-${msg.role}`}>
                          {msg.content}
                        </div>
                        <span className="phd3-msg-time">{formatTime(msg.created_at)}</span>
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>
            </>
          )}
        </div>

      </div>
    </main>
  );
};

export default PromptHistoryDetails;
